import { Component, computed, inject, OnInit, Signal, ViewChild, DestroyRef } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter, map, tap } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { HomeModel } from "./home.model";
import { SyncModel } from "../shared/offline";
import { SideNavComponent } from "../shared/modules";
import { MenuItem } from "../shared/types";
import { selectAmountOFUnreadAnnouncements } from "../shared/state/selectors/announcements.selectors";
import { selectFoundMedicationIncidentsState } from "app/shared/state/selectors/patient-medication.selectors";
import { injectQuery } from "@tanstack/angular-query-experimental";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FeatureService, PermissionsService } from "../shared";
import { CommunicationStore, MessagesStatePollService } from "@intocare-medapp/features/communication/services";

type LoadingState = "loading" | "timeout" | "loaded" | "disconnect" | "retry" | "slow";

/**
 * Inject the menu items, activating the notification counts as needed.
 *
 * This way of working will prevent unnecessary injections/subscriptions, as we only trigger the
 * injection function for menu items that will be active, after filtering them out based on the
 * permissions and feature flags. These check only happen once, because content must be loaded.
 *
 * TODO: This could be further optimized and turned into some utility to easily create menu items.
 * 		Some menu items might stand on their own functional-wise. These can thus be defined/configured
 * 		in their own file/module/thing, and then easily injected where needed, triggering subscriptions.
 * 		In a way, each such menu item is its own view-model/data-store, and be done with an injectable.
 * 		This is exactly why Angular has multi-providers, so you could then have every module that gets
 * 		loaded provide their menu items, which then get injected into the menu based on their config.
 * 		Not that we have to do this, as explicit config has its own benefits, but food for thought.
 * 		Another way we can go about this is through components, which can then be placed into the
 * 		template. If a component gets rendered depends on the component itself, and for things as
 * 		`hasNotifications`, a `viewChildren` query can be used for the calculation.
 */
function injectSideMenu(
	items: (Omit<MenuItem, "notifications"> & { injectNotificationCount?: () => Signal<number> })[],
) {
	const permissionsService = inject(PermissionsService);
	const featureService = inject(FeatureService);

	return items
		.filter(
			(item) =>
				permissionsService.hasEveryPermission(item.permissions || [])() &&
				featureService.hasEveryFeature(item.features || [])(),
		)
		.map(({ injectNotificationCount, ...item }) => ({
			...item,
			...(injectNotificationCount && { notificationCount: injectNotificationCount() }),
		}));
}

@Component({
	templateUrl: "home.component.html",
	styleUrls: ["home.component.scss"],
	standalone: false,
})
export class HomeComponent implements OnInit {
	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);
	private readonly homeModel = inject(HomeModel);
	private readonly syncModel = inject(SyncModel);
	private readonly messagesStatePollService = inject(MessagesStatePollService);

	// @ts-expect-error TS2564
	@ViewChild(SideNavComponent) sideNav: SideNavComponent;

	private readonly _messagesPoll = this.messagesStatePollService.register();

	title$ = this.homeModel.lastActiveRoute$.pipe(
		map((lastActiveRoute) => {
			const menuItem = this.sideMenu.find((item) => item.route.path.join("/") === lastActiveRoute);

			if (!menuItem) {
				return this.sideMenu[0].text;
			}

			return menuItem.text;
		}),
	);

	timeout$ = this.syncModel.timeoutWatcher$;

	message$ = this.syncModel.timeoutWatcher$.pipe(
		// @ts-expect-error TS2345
		map((loadingState: LoadingState) => (loadingState === "slow" ? "general.slow_connection" : "")),
	);

	loading$ = this.timeout$.pipe(
		// @ts-expect-error TS2345
		map(
			(loadingState: LoadingState) => loadingState === "loading" || loadingState === "retry" || loadingState === "slow",
		),
	);

	// @ts-expect-error TS2564
	active: string;

	protected readonly sideMenu = injectSideMenu([
		{
			text: "home.residents",
			icon: "fa fa-home",
			route: {
				path: ["/home", "residents"],
				exact: true,
			},
		},
		{
			text: "home.medication",
			icon: "fa fa-medkit",
			route: {
				path: ["/home", "medication"],
			},
			permissions: ["MEDAPP.MEDICATION"],
			features: ["Medication"],
		},
		{
			text: "home.found_medication",
			icon: "fa fa-info-circle",
			route: {
				path: ["/home", "found-medication-incident-overview"],
			},
			permissions: ["MEDAPP.MEDICATION"],
			features: ["Medication"],
			injectNotificationCount: () => {
				const store = inject(Store);
				const foundMedicationIncidentsSignal = store.selectSignal(selectFoundMedicationIncidentsState);
				return computed(() => {
					return foundMedicationIncidentsSignal().filter((incident) => incident.read === false).length;
				});
			},
		},
		{
			text: "home.dar",
			icon: "fa fa-eye",
			route: {
				path: ["/home", "dar"],
			},
			features: ["Dar"],
		},
		{
			text: "home.alarms",
			icon: "fa fa-bell",
			route: {
				path: ["/home", "alarms"],
				exact: true,
			},
			permissions: ["MEDAPP.CARE"],
			features: ["CallAlarms"],
			injectNotificationCount: () =>
				toSignal(inject(HomeModel).callEvents$.pipe(map((callEvents) => callEvents?.length ?? 0)), { initialValue: 0 }),
		},
		{
			text: "briefing.title",
			icon: "fa fa-book",
			route: {
				path: ["/home", "briefing-history"],
				exact: true,
			},
		},
		{
			text: "home.announcements",
			icon: "fa fa-bullhorn",
			route: {
				path: ["/home", "announcements"],
				exact: true,
			},
			injectNotificationCount: () => {
				const store = inject(Store);
				const selectAmountOfUnreadAnnouncementsSignal = store.selectSignal(selectAmountOFUnreadAnnouncements);
				return computed(() => selectAmountOfUnreadAnnouncementsSignal() || 0);
			},
		},
		{
			text: "home.communication",
			icon: "fa fa-envelope",
			route: {
				path: ["/home", "communication"],
				exact: true,
			},
			permissions: ["MEDAPP.INTERNAL_COMMUNICATION"],
			injectNotificationCount: () => {
				const communicationStore = inject(CommunicationStore);
				const messagesStateQuery = injectQuery(() => communicationStore.getMessagesStateQueryOptions());
				return computed(() => messagesStateQuery.data()?.data?.messages?.unread?.count ?? 0);
			},
		},
	]);

	protected readonly hasNotifications = computed(() => this.sideMenu.some((item) => item.notificationCount?.()));

	retry() {
		this.syncModel.resync();
	}

	showSideNav(event: Event) {
		event.preventDefault();
		this.sideNav.show();
	}

	ngOnInit() {
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				tap((event: NavigationEnd) => this.homeModel.setActiveRoute(event.url)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();
	}
}
